import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import {
  useGetCustomerCreditBalance,
  useDepositCustomerCredit,
  useGetCustomerPaymentMethods,
  useManualCustomerDeposit,
} from "@/api/customer-credit";

const CARD_MAX_CENTS = 50_000;
type FundingMethod = "CASH" | "E_TRANSFER" | "CARD";
interface DepositCreditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: {
    id: string;
    firstName: string;
    lastName: string;
    isTestAccount?: boolean;
  };
  onSuccess?: () => void;
}
export function DepositCreditDialog(props: DepositCreditDialogProps) {
  // A different account is a different deposit session: never carry its draft over.
  return (
    <AccountTopUpDialog
      key={`${props.customer.id}:${Boolean(props.customer.isTestAccount)}`}
      {...props}
    />
  );
}

function AccountTopUpDialog({
  open,
  onOpenChange,
  customer,
  onSuccess,
}: DepositCreditDialogProps) {
  const [method, setMethod] = useState<FundingMethod>("CASH");
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [received, setReceived] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const requestKey = useRef(crypto.randomUUID());
  const submitLock = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const { data: balance } = useGetCustomerCreditBalance(customer.id);
  const { data: paymentMethods, isLoading: isLoadingCards } =
    useGetCustomerPaymentMethods(customer.id);
  const cardDeposit = useDepositCustomerCredit(customer.id);
  const manualDeposit = useManualCustomerDeposit(customer.id);
  useEffect(() => {
    if (!open) return;
    setMethod("CASH");
    setAmount(0);
    setNote("");
    setReference("");
    setReceived(false);
    setPaymentMethodId("");
    requestKey.current = crypto.randomUUID();
  }, [open]);
  const isCard = method === "CARD";
  const pending =
    submitting || cardDeposit.isPending || manualDeposit.isPending;
  const valid =
    Number.isSafeInteger(amount) &&
    amount >= 100 &&
    amount <= (isCard ? CARD_MAX_CENTS : 2_147_483_647) &&
    (isCard
      ? !customer.isTestAccount && !!paymentMethodId
      : received && !!reference.trim());
  const methodLabel = method === "E_TRANSFER" ? "e-transfer" : "cash";
  const referenceLabel =
    method === "E_TRANSFER"
      ? "Transfer confirmation / reference"
      : "Receipt / reference";
  const changeMethod = (value: FundingMethod) => {
    setMethod(value);
    setReference("");
    setReceived(false);
  };
  const submit = async () => {
    if (!valid || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      const result = isCard
        ? await cardDeposit.mutateAsync({
            amountCents: amount,
            paymentMethodId,
            description: note.trim() || undefined,
          })
        : await manualDeposit.mutateAsync({
            amountCents: amount,
            method,
            reference: reference.trim(),
            note: note.trim() || undefined,
            received: true,
            idempotencyKey: requestKey.current,
          });
      toast.success(
        "alreadyRecorded" in result && result.alreadyRecorded
          ? "Deposit already recorded"
          : "Deposit recorded",
        {
          description: `Available credit: ${formatCurrency(result.balanceCents)}`,
        }
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const data = axios.isAxiosError<{ error?: string; message?: string }>(
        error
      )
        ? error.response?.data
        : undefined;
      toast.error("Top-up failed", {
        description:
          data?.message ??
          data?.error ??
          "Retry with the same details or check credit history before recording another deposit.",
      });
      // Preserve requestKey across failures: a lost response must not credit twice.
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!pending) onOpenChange(value);
      }}
    >
      <DialogContent className="sm:max-w-[440px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Top up account</DialogTitle>
          <DialogDescription>
            {customer.firstName} {customer.lastName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Payment method</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={method === "CASH" ? "default" : "outline"}
                aria-pressed={method === "CASH"}
                disabled={pending}
                onClick={() => changeMethod("CASH")}
              >
                Cash
              </Button>
              <Button
                type="button"
                variant={method === "E_TRANSFER" ? "default" : "outline"}
                aria-pressed={method === "E_TRANSFER"}
                disabled={pending}
                onClick={() => changeMethod("E_TRANSFER")}
              >
                E-transfer
              </Button>
              {!customer.isTestAccount && (
                <Button
                  type="button"
                  variant={isCard ? "default" : "outline"}
                  aria-pressed={isCard}
                  disabled={pending}
                  onClick={() => changeMethod("CARD")}
                >
                  Card
                </Button>
              )}
            </div>
            {customer.isTestAccount && (
              <p className="text-xs text-muted-foreground">
                Test accounts accept cash and e-transfer deposits.
              </p>
            )}
          </div>
          {isCard && (
            <div className="space-y-2">
              <Label>Saved card</Label>
              <Select
                value={paymentMethodId}
                onValueChange={setPaymentMethodId}
                disabled={pending || isLoadingCards}
              >
                <SelectTrigger aria-label="Saved card">
                  <SelectValue
                    placeholder={
                      isLoadingCards ? "Loading cards…" : "Select a saved card"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(paymentMethods?.paymentMethods ?? []).map((card) => (
                    <SelectItem key={card.id} value={card.id}>
                      {(card.brand ?? card.type).toUpperCase()} ••••{" "}
                      {card.last4}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLoadingCards && !paymentMethods?.paymentMethods.length && (
                <p className="text-sm text-destructive">
                  No saved cards. Add a card before using a card deposit, or
                  record money received by cash or e-transfer.
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="topup-amount">Amount (CAD)</Label>
            <CurrencyInput
              id="topup-amount"
              value={amount}
              onChange={setAmount}
              placeholder="0.00"
              disabled={pending}
            />
            <p className="text-xs text-muted-foreground">
              Minimum $1.00
              {isCard
                ? ` · maximum ${formatCurrency(CARD_MAX_CENTS)} per card deposit`
                : ""}
            </p>
          </div>
          {!isCard && (
            <div className="space-y-2">
              <Label htmlFor="topup-reference">{referenceLabel}</Label>
              <Input
                id="topup-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={120}
                disabled={pending}
                placeholder={
                  method === "CASH"
                    ? "e.g. CASH-1042"
                    : "Transfer confirmation number"
                }
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="topup-note">Note (optional)</Label>
            <Textarea
              id="topup-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              disabled={pending}
            />
          </div>
          {balance && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="flex justify-between">
                <span>Current balance</span>
                <span>{formatCurrency(balance.balanceCents)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>Balance after top-up</span>
                <span>{formatCurrency(balance.balanceCents + amount)}</span>
              </div>
            </div>
          )}
          {isCard ? (
            <Alert>
              <AlertDescription>
                This charges the selected card immediately. Credit is added
                after payment succeeds.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex items-start gap-2">
              <Checkbox
                id="topup-received"
                checked={received}
                onCheckedChange={(value) => setReceived(value === true)}
                disabled={pending}
              />
              <Label htmlFor="topup-received" className="leading-5">
                I confirm this {methodLabel} has been received.
              </Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button disabled={!valid || pending} onClick={() => void submit()}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCard
              ? `Charge card ${formatCurrency(amount)}`
              : `Record ${methodLabel} deposit`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
